import React, { useState, useEffect, useMemo } from "react";
import {
  TextField,
  Box,
  InputLabel,
  Typography,
  Slider,
  Select,
  MenuItem,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import { colors } from "../utils/const";
import en from "../../locales/en";
import ar from "../../locales/ar";
import { useRouter } from "next/router";
import { api } from "@/utils/Axios";
import { arFont, enFont } from "@/styles/font";
import Image from "next/legacy/image";
import { calcAr_schema } from "./calcAr_schema";
import { calcEn_schema } from "./calcEn_schema";
import useDebounce from "@/hooks/useDebounce";

export default function Calcuation({
  products_packages,
  page,
  show,
  max_amount,
}) {
  const [productPrice, setProductPrice] = useState("");
  const debouncedValue = useDebounce(productPrice, 500);
  const [downPayment, setDownPayment] = useState(0);
  const [duration, setDuration] = useState();
  const [plan, setPlan] = useState();
  const [value, setValue] = useState();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedPlan, setSelectedplane] = useState();
  const [selectedPlaneTitle, setSelectedPlaneTitle] = useState("");
  const router = useRouter();

  const { locale } = router;
  const t = locale === "en" ? en : ar;
  const validateInput = locale === "ar" ? calcAr_schema : calcEn_schema;

  const theme = useMemo(
    () => createTheme({ direction: locale === "ar" ? "rtl" : "ltr" }),
    [locale]
  );

  const calculateMinimumDownPayment = (price, plan) => {
    let percentage;
    switch (page) {
      case "trucks":
      case "home-interior":
      case "home-furniture":
      case "wedding":
      case "watercraft":
      case "maintenance":
      case "motorcycles":
      case "manasik":
        percentage = 0.2;
        break;
      case "green-finance":
        percentage = 0.3;
        break;
      case "mortgage":
        if (plan === 180 || plan === 179) {
          percentage = 0.0;
        } else {
          percentage = 0.25;
        }
        break;
      case "leasing":
      case "auto":
        percentage = 0.1;
        break;
      default:
        percentage = 0.0;
    }
    return price * percentage;
  };

  const getOptions = () => {
    if (page === "maintenance") {
      return [1, 2];
    }
    if (page === "mortgage" && selectedPlan === 336) {
      return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    }

    if (page === "mortgage" && (selectedPlan == 180 || selectedPlan == 179)) {
      return [1, 2, 3, 4, 5];
    }
    if (page === "wedding") {
      return [1, 2, 3];
    }
    if (page === "manasik" && ["عمره", "Umrah"].includes(selectedPlaneTitle)) {
      return [0.5, 1];
    }
    if (page === "manasik" && ["حج", "Hajj"].includes(selectedPlaneTitle)) {
      return [0.5, 1, 2, 3, 4, 5];
    }

    return [1, 2, 3, 4, 5];
  };

  const Calc = async (productPrice, duration, plan, downpay) => {
    setLoading(true);

    try {
      const res = await api.post("/products/general-loan-calculator", {
        amount: +productPrice,
        downPayment: downpay <= productPrice ? downpay : 0,
        packageId: plan,
        tenors: duration * 12,
      });
      setValue(res?.data?.amount);
      setLoading(false);
    } catch (err) {
      setValue(null);
      setLoading(false);
    }
  };

  const checkInputs = (productPrice, duration, plan, downpay) => {
    if (productPrice && duration && plan && downpay >= 0) {
      Calc(productPrice, duration, plan, downpay);
    } else if (productPrice && duration && show == false && downpay >= 0) {
      Calc(productPrice, duration, products_packages[0].packageId, downpay);
    } else {
      setValue(null);
    }
  };

  const handleSelectPlanItem = (e, title) => {
    if (duration > 1 && ["عمره", "Umrah"].includes(title)) setDuration(null);

    setSelectedPlaneTitle(title);
  };

  const handleProductPriceChange = (e) => {
    const re = /^[0-9\b]+$/;
    if (e.target.value === "" || re.test(e.target.value)) {
      let maxAmount = max_amount;
      let minAmount = 0;
      let validation;

      if (page === "manasik") {
        minAmount = 50000;
        maxAmount = 3000000;

        if (["عمره", "Umrah"].includes(selectedPlaneTitle)) {
          maxAmount = 1000000; // Adjust max limit for plan 526
        }
        if (["حج", "Hajj"].includes(selectedPlaneTitle)) {
          maxAmount = 3000000; // Adjust max limit for plan 525
        }
      }
      // Validation function
      validation = validateInput(e.target.value, minAmount, maxAmount, page);

      // Set the product price regardless of validation
      setProductPrice(e.target.value);

      // Check if the validation is successful
      if (validation.valid) {
        setErrorMessage("");
        setDownPayment(calculateMinimumDownPayment(e.target.value));
      } else {
        setErrorMessage(validation.message);
        setDownPayment(0);
      }
    }

    if (e.target.value === "") {
      setDownPayment(0);
    }
  };

  // Trigger the API call when the debounced value changes
  useEffect(() => {
    if (debouncedValue) {
      console.log(debouncedValue);

      checkInputs(
        debouncedValue,
        duration,
        plan,
        calculateMinimumDownPayment(debouncedValue)
      );
    }
  }, [debouncedValue, duration, plan]);

  function valuetext(value) {
    return `${value}°C`;
  }

  const handleDownPaymentChange = (e) => {
    setDownPayment(e.target.value);
  };

  const handleDownPaymentRequest = () => {
    checkInputs(productPrice, duration, plan, downPayment);
  };

  const handleDurationChange = (e) => {
    setDuration(e.target.value);

    checkInputs(productPrice, e.target.value, plan, downPayment);
  };

  const handlePlanChange = (e) => {
    const newPlan = e.target.value;
    setPlan(newPlan);
    setSelectedplane(newPlan);
    if (page === "manasik") {
      let minAmount = 50000;
      let maxAmount = 3000000;

      if (["عمره", "Umrah"].includes(selectedPlaneTitle)) {
        maxAmount = 1000000; // Adjust max limit for plan 526
      }
      if (["حج", "Hajj"].includes(selectedPlaneTitle)) {
        maxAmount = 3000000; // Adjust max limit for plan 525
      }

      // Validation function
      const validation = validateInput(productPrice, minAmount, maxAmount);

      // Check if the validation is successful
      if (validation.valid) {
        setErrorMessage(""); // Clear any previous error messages
        const newDownPayment = calculateMinimumDownPayment(
          productPrice,
          newPlan
        );
        setDownPayment(newDownPayment);
        if ([501, 526].includes(newPlan) && duration > 1) {
          setValue(null);
          return;
        }
        checkInputs(productPrice, duration, newPlan, newDownPayment);
      } else {
        // Display the error message instantly
        setErrorMessage(validation.message);
        setDownPayment(0); // Calculate new down payment
      }
    } else {
      const newDownPayment = calculateMinimumDownPayment(productPrice, newPlan);
      setDownPayment(newDownPayment);
      checkInputs(productPrice, duration, newPlan, newDownPayment);
    }
  };
  return (
    <Box
      className="horizontal-safe-padding vertical-safe-padding"
      sx={{
        bgcolor: colors.white,
        backgroundImage:
          locale === "en"
            ? "url('/bg-lines-en.svg')"
            : "url('/bg-lines-ar.svg')",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Box
        sx={{
          fontSize: "42px",
          color: colors.orange,
          mb: 5,

          fontWeight: "600",
        }}
      >
        {t.calcTitle}
      </Box>

      <Box
        sx={{
          // bgcolor: colors.white,
          mb: 5,
          display: "flex",
          justifyContent: "center",
          flexDirection: {
            xs: "column",
            md: "row",
          },
        }}
      >
        <Box
          data-aos="fade-up"
          // data-aos-delay="100"
          sx={{
            flex: "1 1 0",
            // mx: 5,
            px: 3,
            py: 3,
            bgcolor: "#f8f8f8",
          }}
        >
          <InputLabel
            htmlFor="car-price"
            sx={{ color: colors.blue, mb: 1, fontWeight: "bold" }}
          >
            {t.calc.priceCar}
          </InputLabel>

          <TextField
            sx={{
              bgcolor: "white",
              width: "100%",
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "lightgrey",
                },
                "&:hover fieldset": {
                  borderColor: "lightgrey",
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.blue,
                },
              },
            }}
            value={productPrice}
            onChange={handleProductPriceChange}
            hiddenLabel
            id="car-price"
            size="small"
          />
          <Box
            sx={{
              color: "#d32f2f",
              fontSize: "0.75rem",
              m: 1,
              minHeight: "20px",
            }}
          >
            {errorMessage ?? " "}
          </Box>

          {page !== "education" && (
            <Box sx={{ bgcolor: "white", px: 3, pt: 2, my: 2 }}>
              <Typography
                component="h4"
                variant="subtitle1"
                sx={{ color: colors.blue, fontWeight: "bold" }}
                gutterBottom
              >
                {t.calc.preAmount}
              </Typography>
              <Typography
                component="h4"
                variant="subtitle1"
                sx={{ color: colors.blue, fontWeight: "bold" }}
                gutterBottom
              >
                {!productPrice
                  ? 0
                  : downPayment > productPrice
                  ? 0
                  : downPayment}{" "}
                {t.calc.egyptianPound}
              </Typography>
              <ThemeProvider theme={theme}>
                <Slider
                  aria-label="Down payment slider"
                  valueLabelDisplay="auto"
                  value={
                    !productPrice
                      ? 0
                      : downPayment > productPrice
                      ? 0
                      : downPayment
                  }
                  disabled={
                    !productPrice || productPrice.length < 3 || errorMessage
                  }
                  onChange={handleDownPaymentChange}
                  // onChangeCommitted={handleDownPaymentChangeValue}
                  onChangeCommitted={handleDownPaymentRequest}
                  // onDragStop={handleDownPaymentRequest}
                  sx={{
                    color: colors.blue,
                    mt: 1,
                    "& .MuiSlider-thumb": {
                      marginRight: -2,
                    },
                  }}
                  min={calculateMinimumDownPayment(productPrice, plan)} // Use calculated minimum down payment
                  step={
                    productPrice <= 1000
                      ? 3
                      : productPrice > 1000 && productPrice < 10000
                      ? 100
                      : 1000
                  }
                  max={productPrice ? productPrice : 1000000}
                  getAriaValueText={valuetext}
                />
              </ThemeProvider>
            </Box>
          )}
          <Box sx={{ display: "flex", justifyContent: "space-between", pt: 2 }}>
            {show != false && (
              <Box
                sx={{
                  width: "100%",
                }}
              >
                <Typography
                  variant="subtitle1"
                  component="h4"
                  sx={{
                    color: colors.blue,
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                  }}
                  gutterBottom
                >
                  {page == "manasik" ? t.purpose : t.calc.plan}
                </Typography>

                <Select
                  value={plan ?? " "}
                  sx={{
                    width: "100%",

                    ".MuiOutlinedInput-notchedOutline": {
                      borderColor: "lightfrey",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.blue,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.blue,
                    },
                    ".MuiSvgIcon-root ": {
                      fill: colors.blue,
                    },
                  }}
                  size="small"
                  onChange={handlePlanChange}
                >
                  {products_packages &&
                    products_packages.map((item) => (
                      <MenuItem
                        key={item.packageId}
                        value={item.packageId}
                        onClick={(e) => handleSelectPlanItem(e, item.title)}
                        className={
                          locale == "en" ? enFont.className : arFont.className
                        }
                        sx={{ direction: locale == "ar" ? "rtl" : "ltr" }}
                      >
                        {item.title}
                      </MenuItem>
                    ))}
                </Select>
              </Box>
            )}
            <Box
              sx={{
                width: "100%",
                mx: 1,
              }}
            >
              <Typography
                variant="subtitle1"
                component="h4"
                sx={{ color: colors.blue, fontWeight: "bold" }}
                gutterBottom
              >
                {t.calc.duration}
              </Typography>
              <Select
                value={duration ?? " "}
                sx={{
                  width: "100%",
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "lightfrey",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.blue,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.blue,
                  },
                  ".MuiSvgIcon-root ": {
                    fill: colors.blue,
                  },
                }}
                size="small"
                onChange={handleDurationChange}
              >
                {getOptions().map((value) => (
                  <MenuItem
                    key={value}
                    value={value}
                    className={
                      locale === "en" ? enFont.className : arFont.className
                    }
                    sx={{ direction: locale === "ar" ? "rtl" : "ltr" }}
                  >
                    {value >= 1
                      ? t.calc[`year${value}`]
                      : t.calc[`month${value * 12}`]}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
          <Box
            sx={{
              py: 2,
              mt: 3,
              borderRadius: 2,
              px: 5,
              bgcolor: "#ebebed",
              textAlign: "center",
            }}
          >
            <Typography
              variant="subtitle2"
              component="h4"
              sx={{ color: colors.blue }}
              gutterBottom
            >
              {t.calc.monthlyAmount}
            </Typography>
            {loading ? (
              <div className="box-loading">
                <span className="loader"></span>
              </div>
            ) : (
              <Typography variant="h5" sx={{ color: colors.blue }} gutterBottom>
                {value > 0 ? (
                  <>
                    {value} {t.calc.egyptianPound}
                  </>
                ) : (
                  "----"
                )}
              </Typography>
            )}
          </Box>
        </Box>
        <Box
          data-aos="fade-up"
          sx={{
            position: "relative",
            height: "auto",
            minHeight: "400px",
            mx: "auto",
            width: {
              xs: "250px",
              sm: "300px",
              md: "480px",
            },
            mb: "30px",
            flex: "1 1 0",
            mt: {
              md: 1,
              xs: 4,
            },
            flexGrow: 2,
          }}
        >
          <Image
            loading="lazy"
            src={locale === "ar" ? "/calculator.png" : "/calculator-en.png"}
            alt="إحسب قسطك في ثواني"
            sizes="100vw"
            layout="fill"
            objectFit="contain"
          />
        </Box>
      </Box>
    </Box>
  );
}
